import React, { useState, useEffect } from "react";
import { Plus, Search, Pin, Trash2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { useNotes } from "../lib/notes-context";
import ConfirmDialog from "./ConfirmDialog";

const COLOR_BAR = {
    green: "bg-emerald-500",
    blue: "bg-sky-500",
    purple: "bg-violet-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
    slate: "bg-slate-400",
    "": "bg-transparent",
};

function relativeDate(iso) {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "agora";
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24 && now.getDate() === d.getDate()) return `${hours} h`;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

/**
 * Painel de notas — usado como tab na sidebar/drawer e na página /notas.
 * variant="sidebar" (lista compacta) | "page" (cards maiores, grid no desktop).
 * onItemOpen: chamado ao abrir/criar nota (ex.: fechar o drawer mobile).
 */
export default function NotesPanel({ variant = "sidebar", onItemOpen }) {
    const { notes, loading, ensureLoaded, openNote, openNewNote, togglePin, removeNote } = useNotes();
    const [q, setQ] = useState("");
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

    const query = q.trim().toLowerCase();
    const filtered = query
        ? notes.filter((n) =>
            (n.title || "").toLowerCase().includes(query) ||
            (n.preview || "").toLowerCase().includes(query))
        : notes;
    const pinnedNotes = filtered.filter((n) => n.pinned);
    const otherNotes = filtered.filter((n) => !n.pinned);

    const handleNew = () => { openNewNote(); onItemOpen?.(); };
    const handleOpen = (n) => { openNote(n); onItemOpen?.(); };

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await removeNote(toDelete.id);
            toast.success("Nota excluída.");
        } catch {
            toast.error("Não foi possível excluir a nota.");
        } finally {
            setDeleting(false);
            setToDelete(null);
        }
    };

    const isPage = variant === "page";

    return (
        <div className={isPage ? "" : "px-1"} data-testid="notes-panel">
            {/* Ações do topo */}
            <div className={`flex flex-col gap-2 ${isPage ? "mb-4" : "px-2 pb-2"}`}>
                <button
                    type="button"
                    onClick={handleNew}
                    data-testid="new-note-btn"
                    className={`w-full rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 border border-primary/40 text-primary hover:bg-primary/10 transition-colors ${isPage ? "py-3 text-sm" : "py-2"}`}
                >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                    Nova nota
                </button>
                {(notes.length > 3 || query) && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar notas…"
                            data-testid="notes-search"
                            className="w-full bg-[#141414] border border-border rounded-full pl-8 pr-3 py-2 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 transition-colors"
                        />
                    </div>
                )}
            </div>

            {/* Skeleton */}
            {loading && notes.length === 0 && (
                <div className="flex flex-col gap-2 px-2" aria-busy="true">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="rounded-md border border-border p-3">
                            <div className="skeleton-bar h-3 w-2/3 rounded mb-2" />
                            <div className="skeleton-bar h-2.5 w-full rounded" />
                        </div>
                    ))}
                </div>
            )}

            {/* Vazio */}
            {!loading && notes.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 px-3 py-10 text-center text-muted-foreground">
                    <StickyNote className="w-6 h-6 opacity-60" strokeWidth={1.5} />
                    <div className="text-xs">Nenhuma nota ainda</div>
                    <div className="text-[10px] text-muted-foreground/60">Crie sua primeira nota acima.</div>
                </div>
            )}

            {!loading && notes.length > 0 && filtered.length === 0 && (
                <div className="px-3 py-8 text-center text-xs text-muted-foreground">Nada encontrado para “{q}”.</div>
            )}

            {/* Fixadas */}
            {pinnedNotes.length > 0 && (
                <NoteGroup label="Fixadas" isPage={isPage}>
                    {pinnedNotes.map((n) => (
                        <NoteRow key={n.id} note={n} isPage={isPage} onOpen={() => handleOpen(n)} onPin={() => togglePin(n)} onDelete={() => setToDelete(n)} />
                    ))}
                </NoteGroup>
            )}

            {/* Demais */}
            {otherNotes.length > 0 && (
                <NoteGroup label={pinnedNotes.length > 0 ? "Outras" : "Notas"} isPage={isPage}>
                    {otherNotes.map((n) => (
                        <NoteRow key={n.id} note={n} isPage={isPage} onOpen={() => handleOpen(n)} onPin={() => togglePin(n)} onDelete={() => setToDelete(n)} />
                    ))}
                </NoteGroup>
            )}

            <ConfirmDialog
                open={!!toDelete}
                title="Excluir nota?"
                description={toDelete?.title ? `“${toDelete.title}” será excluída permanentemente.` : "A nota será excluída permanentemente."}
                confirmLabel="Excluir"
                destructive
                loading={deleting}
                onConfirm={confirmDelete}
                onCancel={() => setToDelete(null)}
                testId="notes-delete-confirm"
            />
        </div>
    );
}

function NoteGroup({ label, isPage, children }) {
    return (
        <div className={isPage ? "mb-5" : "mb-3"}>
            <div className={`text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground/70 ${isPage ? "pb-2" : "px-3 py-1"}`}>{label}</div>
            <div className={isPage ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" : "flex flex-col gap-1.5 px-2"}>
                {children}
            </div>
        </div>
    );
}

function NoteRow({ note, isPage, onOpen, onPin, onDelete }) {
    const bar = COLOR_BAR[note.color || ""] || COLOR_BAR[""];
    return (
        <div
            className={`group relative rounded-md border border-border bg-[#111111] hover:border-primary/40 transition-colors overflow-hidden ${isPage ? "" : ""}`}
            data-testid={`note-row-${note.id}`}
        >
            {note.color ? <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${bar}`} /> : null}
            <button
                type="button"
                onClick={onOpen}
                className={`w-full text-left ${isPage ? "px-4 py-3.5" : "px-3 py-2.5"}`}
                data-testid={`note-open-${note.id}`}
            >
                <div className="flex items-center gap-1.5 pr-12">
                    {note.pinned && <Pin className="w-3 h-3 text-primary shrink-0 fill-current" />}
                    <span className={`font-semibold truncate ${isPage ? "text-sm" : "text-[13px]"}`}>
                        {note.title || "Sem título"}
                    </span>
                </div>
                {note.preview ? (
                    <div className={`text-muted-foreground mt-0.5 ${isPage ? "text-xs line-clamp-3" : "text-[11px] truncate"}`}>
                        {note.preview}
                    </div>
                ) : (
                    <div className={`text-muted-foreground/40 italic mt-0.5 ${isPage ? "text-xs" : "text-[11px]"}`}>Nota vazia</div>
                )}
                <div className="text-[10px] font-mono text-muted-foreground/50 mt-1">{relativeDate(note.updated_at)}</div>
            </button>
            {/* Ações rápidas */}
            <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onPin(); }}
                    aria-label={note.pinned ? "Desafixar" : "Fixar"}
                    title={note.pinned ? "Desafixar" : "Fixar"}
                    data-testid={`note-pin-${note.id}`}
                    className={`p-1.5 rounded-sm transition-colors ${note.pinned ? "text-primary" : "text-muted-foreground hover:text-primary"} hover:bg-primary/10`}
                >
                    <Pin className={`w-3.5 h-3.5 ${note.pinned ? "fill-current" : ""}`} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    aria-label="Excluir nota"
                    title="Excluir"
                    data-testid={`note-delete-${note.id}`}
                    className="p-1.5 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}
